import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Card,
  Statistic,
  Tag,
  Typography,
  Row,
  Col,
  Button,
  Input,
  Select,
  Space,
  Empty,
  message,
  Spin,
} from "antd";
import * as XLSX from "xlsx";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  DollarOutlined,
  FundOutlined,
  RiseOutlined,
  BankOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Title, Text } = Typography;

const zambianColors = {
  green: "#4d8c4a",
  red: "#d16b6b",
  orange: "#e99e54",
  black: "#444444",
  lightGreen: "#80b782",
  lightOrange: "#f4c28e",
};

const DIRECTUS_URL = "https://zedlenders.pickmesms.com";
const JUTEM_COMPANY_ID = 22;

const JutemLoansPage = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();
  const [amortizations, setAmortizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedYear, setSelectedYear] = useState(
    String(new Date().getFullYear()),
  );
  const [selectedMonth, setSelectedMonth] = useState(
    moment().format("YYYY-MM"),
  );
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedLoanStatus, setSelectedLoanStatus] = useState(null);

  const token = localStorage.getItem("token");

  const fetchAmortizations = async () => {
    try {
      setLoading(true);
      // Fetch amortizations with the related loan to get original principal and borrower name
      const res = await axios.get(`${DIRECTUS_URL}/items/amortization`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          "filter[loan_reference][company][_eq]": JUTEM_COMPANY_ID,
          fields:
            "*,loan_reference.id,loan_reference.amount,loan_reference.loan_status,loan_reference.borrower.first_name,loan_reference.borrower.last_name",
          limit: -1,
          sort: "-due_date",
        },
      });
      setAmortizations(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        setAmortizations([]);
      } else {
        message.error("Failed to load amortization data");
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAmortizations();
    }
  }, [token]);

  // Derive borrower name from the related loan's borrower
  const getBorrowerName = (row) => {
    const loan = row.loan_reference;
    if (!loan) return "N/A";
    const b = loan.borrower;
    if (b && (b.first_name || b.last_name)) {
      return `${b.first_name || ""} ${b.last_name || ""}`.trim();
    }
    return "N/A";
  };

  // Get original principal from the related loan
  const getOriginalPrincipal = (row) => {
    return parseFloat(row.loan_reference?.amount || 0);
  };

  // Extract YYYY-MM from due_date for filtering
  const getMonthKey = (row) => {
    if (!row.due_date) return null;
    return row.due_date.substring(0, 7);
  };

  const availableYears = useMemo(() => {
    const years = [
      ...new Set(
        amortizations.map((a) => a.due_date?.substring(0, 4)).filter(Boolean),
      ),
    ]
      .sort()
      .reverse();
    return years;
  }, [amortizations]);

  const availableMonths = useMemo(() => {
    let source = amortizations;
    if (selectedYear) {
      source = source.filter((a) => a.due_date?.startsWith(selectedYear));
    }
    const months = [
      ...new Set(source.map((a) => getMonthKey(a)).filter(Boolean)),
    ]
      .sort()
      .reverse();
    return months;
  }, [amortizations, selectedYear]);

  const filteredData = useMemo(() => {
    let filtered = [...amortizations];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter((a) =>
        getBorrowerName(a).toLowerCase().includes(search),
      );
    }
    if (selectedYear) {
      filtered = filtered.filter((a) => a.due_date?.startsWith(selectedYear));
    }
    if (selectedMonth) {
      filtered = filtered.filter((a) => getMonthKey(a) === selectedMonth);
    }
    if (selectedStatus) {
      const statusLower = selectedStatus.toLowerCase();
      filtered = filtered.filter(
        (a) => (a.status || "").toLowerCase() === statusLower,
      );
    }
    if (selectedLoanStatus !== null) {
      filtered = filtered.filter(
        (a) => a.loan_reference?.loan_status === selectedLoanStatus,
      );
    }

    // Sort: Due today first, then by due date
    filtered.sort((a, b) => {
      const dateA = moment(a.due_date);
      const dateB = moment(b.due_date);
      const today = moment();

      const isDueTodayA = dateA.isSame(today, "day");
      const isDueTodayB = dateB.isSame(today, "day");

      // Due today items come first
      if (isDueTodayA && !isDueTodayB) return -1;
      if (!isDueTodayA && isDueTodayB) return 1;

      // Otherwise sort by date (earliest first)
      return dateA.diff(dateB);
    });

    return filtered;
  }, [
    amortizations,
    searchText,
    selectedYear,
    selectedMonth,
    selectedStatus,
    selectedLoanStatus,
  ]);

  const stats = useMemo(() => {
    const paidRows = filteredData.filter((a) =>
      ["paid", "cleared"].includes((a.status || "").toLowerCase()),
    );
    return {
      totalAmountDue: filteredData.reduce(
        (s, a) => s + parseFloat(a.amount_due || 0),
        0,
      ),
      totalExpected: filteredData.reduce(
        (s, a) => s + parseFloat(a.expected_amount || 0),
        0,
      ),
      totalProfit: filteredData.reduce(
        (s, a) => s + parseFloat(a.profit || 0),
        0,
      ),
      totalOriginalPrincipal: filteredData.reduce(
        (s, a) => s + getOriginalPrincipal(a),
        0,
      ),
      totalPaymentsReceived: paidRows.reduce(
        (s, a) => s + parseFloat(a.expected_amount || 0),
        0,
      ),
    };
  }, [filteredData]);

  const handleExport = () => {
    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }

    const exportData = filteredData.map((r, i) => ({
      "#": i + 1,
      Name: getBorrowerName(r),
      "Principal (ZMW)": parseFloat(getOriginalPrincipal(r) || 0).toFixed(2),
      "Amount Due (ZMW)": parseFloat(r.amount_due || 0).toFixed(2),
      "Interest Rate (%)": r.interest_rate || "",
      "Expected Amount (ZMW)": parseFloat(r.expected_amount || 0).toFixed(2),
      "Due Date": r.due_date,
      "Profit (ZMW)": parseFloat(r.profit || 0).toFixed(2),
      Status: r.status,
      Month: r.month || getMonthKey(r),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jutem Amortization");
    XLSX.writeFile(
      wb,
      `Jutem_Amortization_${moment().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedStatus(null);
    setSelectedLoanStatus(null);
  };

  const fmtK = (v) =>
    `K ${(v || 0).toLocaleString("en-ZM", { minimumFractionDigits: 2 })}`;

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "Name",
      key: "name",
      sorter: (a, b) => getBorrowerName(a).localeCompare(getBorrowerName(b)),
      render: (_, row) => {
        const isSettled = row.loan_reference?.loan_status === 10;
        const isOverdue = row.loan_reference?.loan_status === 8;
        return (
          <Space>
            <Text strong>{getBorrowerName(row)}</Text>
            {isSettled && (
              <Tag color="success" style={{ fontWeight: "bold" }}>
                🎉 SETTLED
              </Tag>
            )}
            {isOverdue && (
              <Tag color="error" style={{ fontWeight: "bold" }}>
                ⚠️ OVERDUE
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Total Amt Loaned",
      key: "original_principal",
      sorter: (a, b) => getOriginalPrincipal(a) - getOriginalPrincipal(b),
      render: (_, row) => fmtK(getOriginalPrincipal(row)),
      align: "right",
    },
    {
      title: "Principal",
      dataIndex: "amount_due",
      key: "amount_due",
      sorter: (a, b) => (a.amount_due || 0) - (b.amount_due || 0),
      render: (v) => fmtK(v),
      align: "right",
    },
    {
      title: "Interest %",
      dataIndex: "interest_rate",
      key: "interest_rate",
      render: (v) => (v ? `${v}%` : "-"),
      align: "right",
    },
    {
      title: "Expected Amount",
      dataIndex: "expected_amount",
      key: "expected_amount",
      sorter: (a, b) => (a.expected_amount || 0) - (b.expected_amount || 0),
      render: (v) => fmtK(v),
      align: "right",
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      sorter: (a, b) => (a.due_date || "").localeCompare(b.due_date || ""),
      render: (v) => {
        if (!v) return "-";
        const isDueToday = moment(v).isSame(moment(), "day");
        return (
          <Space direction="vertical" size={2}>
            <Text>{moment(v).format("DD MMM YYYY")}</Text>
            {isDueToday && (
              <Tag
                color="orange"
                style={{
                  fontWeight: "bold",
                  animation: "pulse 2s ease-in-out infinite",
                  margin: 0,
                }}
              >
                📅 DUE TODAY
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Profit",
      dataIndex: "profit",
      key: "profit",
      sorter: (a, b) => (a.profit || 0) - (b.profit || 0),
      render: (v) => (
        <Text style={{ color: zambianColors.green }}>{fmtK(v)}</Text>
      ),
      align: "right",
    },
    {
      title: "Payment Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const isPaid = s && ["paid", "cleared"].includes(s.toLowerCase());
        return (
          <Tag
            color={isPaid ? "green" : "red"}
            icon={isPaid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          >
            {s || "Pending"}
          </Tag>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading Jutem Amortization...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "16px" }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Jutem Loans Book
          </Title>
          <Text type="secondary">
            {filteredData.length} amortization entries
            {selectedMonth
              ? ` for ${moment(selectedMonth, "YYYY-MM").format("MMMM YYYY")}`
              : selectedYear
                ? ` for ${selectedYear}`
                : ` across ${availableMonths.length} month(s)`}
          </Text>
        </Col>
        <Col>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export
          </Button>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.green,
              boxShadow: "0 2px 8px rgba(77, 140, 74, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Principal
                </span>
              }
              value={stats.totalOriginalPrincipal}
              precision={2}
              prefix={<BankOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.lightGreen,
              boxShadow: "0 2px 8px rgba(128, 183, 130, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Total Amount Due
                </span>
              }
              value={stats.totalAmountDue}
              precision={2}
              prefix={
                <DollarOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.orange,
              boxShadow: "0 2px 8px rgba(233, 158, 84, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Total Expected
                </span>
              }
              value={stats.totalExpected}
              precision={2}
              prefix={<FundOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.black,
              boxShadow: "0 2px 8px rgba(68, 68, 68, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Total Profit
                </span>
              }
              value={stats.totalProfit}
              precision={2}
              prefix={<RiseOutlined style={{ color: "#fff", fontSize: 24 }} />}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              suffix={<span style={{ color: "#fff", fontSize: 14 }}>ZMW</span>}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Year"
              value={selectedYear}
              onChange={(val) => {
                setSelectedYear(val);
                setSelectedMonth(null);
              }}
              allowClear
              style={{ width: "100%" }}
            >
              {availableYears.map((y) => (
                <Select.Option key={y} value={y}>
                  {y}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={5} md={4}>
            <Select
              placeholder="Month"
              value={selectedMonth}
              onChange={setSelectedMonth}
              allowClear
              style={{ width: "100%" }}
            >
              {availableMonths.map((m) => (
                <Select.Option key={m} value={m}>
                  {moment(m, "YYYY-MM").format("MMM YYYY")}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Payment Status"
              value={selectedStatus}
              onChange={setSelectedStatus}
              allowClear
              style={{ width: "100%" }}
            >
              <Select.Option value="paid">Paid</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="unsettled">Unsettled</Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Loan Status"
              value={selectedLoanStatus}
              onChange={setSelectedLoanStatus}
              allowClear
              style={{ width: "100%" }}
            >
              <Select.Option value={10}>🎉 Settled</Select.Option>
              <Select.Option value={7}>Active</Select.Option>
              <Select.Option value={8}>Overdue</Select.Option>
              <Select.Option value={9}>Defaulted</Select.Option>
            </Select>
          </Col>
          {(searchText ||
            selectedYear ||
            selectedMonth ||
            selectedStatus ||
            selectedLoanStatus !== null) && (
            <Col>
              <Button type="link" onClick={clearFilters} danger>
                Clear Filters
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Data Table */}
      <Card title={`Jutem Amortization (${filteredData.length} entries)`}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          rowClassName={(record) => {
            const loanStatus = record.loan_reference?.loan_status;
            if (loanStatus === 10) return "settled-loan-row";
            if (loanStatus === 8) return "overdue-loan-row";
            return "";
          }}
          onRow={(record) => ({
            onClick: () => {
              const loanId = record.loan_reference?.id || record.loan_reference;
              if (loanId) navigate(`/loan/${loanId}`);
            },
            style: { cursor: "pointer" },
          })}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (t) => `${t} entries`,
          }}
          scroll={{ x: true }}
          summary={() => {
            const totalPrincipal = filteredData.reduce(
              (s, r) => s + getOriginalPrincipal(r),
              0,
            );
            const totalDue = filteredData.reduce(
              (s, r) => s + parseFloat(r.amount_due || 0),
              0,
            );
            const totalExpected = filteredData.reduce(
              (s, r) => s + parseFloat(r.expected_amount || 0),
              0,
            );
            const totalProfit = filteredData.reduce(
              (s, r) => s + parseFloat(r.profit || 0),
              0,
            );
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: "#f5f5f5" }}>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1}>
                    <Text strong>TOTALS</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong>{fmtK(totalPrincipal)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>{fmtK(totalDue)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong>{fmtK(totalExpected)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} align="right">
                    <Text strong style={{ color: zambianColors.green }}>
                      {fmtK(totalProfit)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
          locale={{
            emptyText: (
              <Empty
                description="No amortization data found."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default JutemLoansPage;

// Add CSS for settled and overdue loan styling
const style = document.createElement("style");
style.textContent = `
  /* Settled loan styling */
  .settled-loan-row {
    background-color: #f0f9ff !important;
    border-left: 4px solid #52c41a !important;
  }

  .settled-loan-row:hover {
    background-color: #e6f7ff !important;
  }

  .settled-loan-row td {
    position: relative;
  }

  .settled-loan-row td:first-child::before {
    content: "✓";
    position: absolute;
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
    color: #52c41a;
    font-weight: bold;
    font-size: 16px;
  }

  /* Overdue loan styling */
  .overdue-loan-row {
    background-color: #fff1f0 !important;
    border-left: 4px solid #ff4d4f !important;
  }

  .overdue-loan-row:hover {
    background-color: #ffe7e6 !important;
  }

  .overdue-loan-row td {
    position: relative;
  }

  .overdue-loan-row td:first-child::before {
    content: "⚠";
    position: absolute;
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
    color: #ff4d4f;
    font-weight: bold;
    font-size: 16px;
  }

  /* Pulse animation for due today badge */
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
  }
`;
if (!document.head.querySelector("[data-loan-status-style]")) {
  style.setAttribute("data-loan-status-style", "true");
  document.head.appendChild(style);
}
